/**
 * Frontend Complet - Call Center Cognitif
 * Toutes les 75 routes du backend unifié
 */

import React, { useState, useEffect, CSSProperties, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

// Backend URL
const API_URL = 'https://callcenter-app.quantisensia.workers.dev';

// ============================================
// TYPES
// ============================================

interface Tab {
  id: string;
  label: string;
  icon: string;
  color: string;
}

interface SubTab {
  id: string;
  label: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Health
interface HealthData {
  status: string;
  service: string;
  version: string;
  environment: string;
  timestamp: number;
}

// Orchestration
interface CallInitData {
  sessionId: string;
  routing: { agentId: string; agentType: string };
  analysis: { intent: string; priority: string };
}

// RAG
interface EmbeddingData {
  success: boolean;
  embedding: number[];
  dimensions: number;
  model: string;
}

interface SearchResult {
  id: string;
  score: number;
  metadata?: any;
}

interface DocumentData {
  id: string;
  title: string;
  content?: string;
  source?: string;
  source_type?: string;
}

// Analytics
interface AnalyticsLive {
  activeCalls: number;
  waitingCalls: number;
  agentsAvailable: number;
  agentsBusy: number;
  avgWaitTime: number;
}

interface AnalyticsDaily {
  totalCalls: number;
  resolvedCalls: number;
  avgDuration: number;
  csat: number;
}

interface SentimentData {
  sentiment: string;
  score: number;
  confidence: number;
  keywords?: string[];
  emotion?: string;
}

// Agents
interface Agent {
  id: string;
  name: string;
  type: string;
  specialty: string[];
  status: 'available' | 'busy' | 'offline';
}

interface Tool {
  id: string;
  name: string;
  description: string;
}

// Training
interface Persona {
  id: string;
  name: string;
  difficulty: string;
  description: string;
}

interface Scenario {
  id: string;
  name: string;
  description: string;
  difficulty: string;
}

interface TraineeProgress {
  traineeId: string;
  totalSessions: number;
  averageScore: number;
  trend: string;
}

// QA
interface Scorecard {
  sessionId: string;
  overallScore: number;
  categories: any[];
  strengths: string[];
  improvements: string[];
}

interface ComplianceCheck {
  passed: boolean;
  violations: any[];
  pciDetected: boolean;
}

// Memory
interface Customer {
  customer_id: string;
  phone_number: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  vip?: number;
}

interface Summary {
  subject: string;
  actions: string[];
  resolution: string;
  followUpRequired: boolean;
}

// Actions
interface Ticket {
  id: string;
  subject: string;
  status: string;
  priority: string;
}

interface Invoice {
  id: string;
  amount: number;
  status: string;
}

// ============================================
// STYLES
// ============================================

const styles: Record<string, CSSProperties> = {
  container: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    minHeight: '100vh',
    backgroundColor: '#050505',
    color: '#f3f4f6',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '24px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  logoIcon: { fontSize: '32px' },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #00f0ff, #7000ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'rgba(0,255,136,0.1)',
    color: '#00ff88',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 600,
  },
  layout: {
    display: 'flex',
    flex: 1,
    minHeight: 'calc(100vh - 88px)',
  },
  sidebar: {
    width: '300px',
    padding: '24px',
    borderRight: '1px solid rgba(255,255,255,0.05)',
    backgroundColor: 'rgba(0,0,0,0.2)',
    overflowY: 'auto' as const,
  },
  navSection: { marginBottom: '32px' },
  navSectionTitle: {
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '1.5px',
    color: '#6b7280',
    marginBottom: '12px',
    fontWeight: 600,
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 16px',
    marginBottom: '8px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: '12px',
    color: '#9ca3af',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
  },
  navButtonActive: {
    backgroundColor: 'rgba(0,240,255,0.08)',
    color: '#00f0ff',
    border: '1px solid rgba(0,240,255,0.3)',
  },
  subNavButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 16px',
    marginBottom: '4px',
    marginLeft: '24px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: '8px',
    color: '#6b7280',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontSize: '13px',
    transition: 'all 0.2s ease',
  },
  subNavButtonActive: {
    backgroundColor: 'rgba(0,240,255,0.05)',
    color: '#00f0ff',
  },
  icon: { fontSize: '20px', width: '24px', textAlign: 'center' as const },
  main: {
    flex: 1,
    padding: '40px',
    overflow: 'auto' as const,
  },
  pageTitle: {
    fontSize: '32px',
    fontWeight: 700,
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageDescription: {
    color: '#9ca3af',
    fontSize: '15px',
    marginBottom: '32px',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  button: {
    padding: '14px 28px',
    backgroundColor: '#00f0ff',
    color: '#000',
    border: 'none',
    borderRadius: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    marginRight: '12px',
    marginBottom: '12px',
  },
  buttonSecondary: {
    padding: '14px 28px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#f3f4f6',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    marginRight: '12px',
    marginBottom: '12px',
  },
  buttonDanger: {
    padding: '14px 28px',
    backgroundColor: '#ff0055',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    marginRight: '12px',
    marginBottom: '12px',
  },
  buttonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  input: {
    padding: '14px 16px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: '#f3f4f6',
    fontSize: '14px',
    width: '100%',
    marginBottom: '16px',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    padding: '14px 16px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: '#f3f4f6',
    fontSize: '14px',
    width: '100%',
    minHeight: '120px',
    marginBottom: '16px',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
  },
  pre: {
    backgroundColor: 'rgba(0,0,0,0.4)',
    padding: '20px',
    borderRadius: '12px',
    overflow: 'auto' as const,
    fontSize: '13px',
    marginTop: '20px',
    border: '1px solid rgba(255,255,255,0.05)',
  },
  error: {
    color: '#ff0055',
    padding: '16px',
    backgroundColor: 'rgba(255,0,85,0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(255,0,85,0.2)',
  },
  success: {
    color: '#00ff88',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '32px',
  },
  statCard: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '48px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #00f0ff, #7000ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
  },
  statLabel: {
    fontSize: '13px',
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    color: '#9ca3af',
    fontWeight: 600,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#9ca3af',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
  },
  badgeEasy: { backgroundColor: 'rgba(0,255,136,0.15)', color: '#00ff88' },
  badgeMedium: { backgroundColor: 'rgba(255,170,0,0.15)', color: '#ffaa00' },
  badgeHard: { backgroundColor: 'rgba(255,0,85,0.15)', color: '#ff0055' },
  agentCard: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
  },
  agentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  agentName: { fontSize: '16px', fontWeight: 600 },
  agentType: { fontSize: '13px', color: '#9ca3af' },
  agentStatus: {
    fontSize: '12px',
    padding: '4px 12px',
    borderRadius: '20px',
  },
  statusAvailable: { backgroundColor: 'rgba(0,255,136,0.15)', color: '#00ff88' },
  statusBusy: { backgroundColor: 'rgba(255,170,0,0.15)', color: '#ffaa00' },
  statusOffline: { backgroundColor: 'rgba(107,114,128,0.15)', color: '#6b7280' },
  formGroup: { marginBottom: '20px' },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#f3f4f6',
  },
  row: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
  },
  col: { flex: 1 },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

async function apiCall<T>(
  endpoint: string,
  method: string = 'GET',
  body?: any
): Promise<ApiResponse<T>> {
  try {
    const options: RequestInit = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (body) options.body = JSON.stringify(body);

    const res = await fetch(`${API_URL}${endpoint}`, options);
    const data = await res.json();
    return data;
  } catch (err: unknown) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

// ============================================
// COMPONENTS
// ============================================

function Header(): JSX.Element {
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch(`${API_URL}/health`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          setIsOnline(data.status === 'ok');
        } else {
          setIsOnline(false);
        }
      } catch (err) {
        console.error('[Health Check Error]', err);
        setIsOnline(false);
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    checkHealth();
  }, []);

  return (
    <header style={styles.header}>
      <div style={styles.logo}>
        <span style={styles.logoIcon}>🎧</span>
        <h1 style={styles.title}>Call Center Cognitif</h1>
      </div>
      <div style={{
        ...styles.statusBadge,
        backgroundColor: isOnline ? 'rgba(0,255,136,0.1)' : 'rgba(255,0,85,0.1)',
        color: isOnline ? '#00ff88' : '#ff0055',
      }}>
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isOnline ? '#00ff88' : '#ff0055',
          display: 'inline-block',
        }} />
        {isOnline ? 'Backend Online' : error ? `Error: ${error}` : 'Backend Offline'}
      </div>
    </header>
  );
}

interface SidebarProps {
  activeTab: string;
  activeSubTab: string;
  setActiveTab: (tab: string) => void;
  setActiveSubTab: (subTab: string) => void;
}

function Sidebar({ activeTab, activeSubTab, setActiveTab, setActiveSubTab }: SidebarProps): JSX.Element {
  const tabs: (Tab & { subTabs?: SubTab[] })[] = [
    { id: 'health', label: 'Health', icon: '🏥', color: '#00f0ff' },
    { 
      id: 'orchestration', 
      label: 'Orchestration', 
      icon: '🎯', 
      color: '#7000ff',
      subTabs: [
        { id: 'call-initiate', label: 'Init Call' },
        { id: 'call-analyze', label: 'Analyze Parallel' },
        { id: 'call-status', label: 'ADK Status' },
      ]
    },
    { 
      id: 'rag', 
      label: 'RAG / Vectorize', 
      icon: '🔍', 
      color: '#00ff88',
      subTabs: [
        { id: 'embedding', label: 'Embeddings' },
        { id: 'search', label: 'Search' },
        { id: 'ingest', label: 'Ingest' },
        { id: 'documents', label: 'Documents' },
      ]
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: '📊', 
      color: '#ffaa00',
      subTabs: [
        { id: 'analytics-live', label: 'Live Stats' },
        { id: 'analytics-daily', label: 'Daily Stats' },
        { id: 'sentiment', label: 'Sentiment' },
        { id: 'tracking', label: 'Tracking' },
      ]
    },
    { 
      id: 'agents', 
      label: 'Agents & Tools', 
      icon: '🤖', 
      color: '#ff0055',
      subTabs: [
        { id: 'agents-list', label: 'Agents List' },
        { id: 'tools', label: 'Tools' },
      ]
    },
    { 
      id: 'memory', 
      label: 'Memory', 
      icon: '🧠', 
      color: '#00f0ff',
      subTabs: [
        { id: 'short-term', label: 'Short-Term' },
        { id: 'long-term', label: 'Long-Term' },
        { id: 'summary', label: 'Summary' },
      ]
    },
    { 
      id: 'qa', 
      label: 'QA & Compliance', 
      icon: '✅', 
      color: '#7000ff',
      subTabs: [
        { id: 'scorecards', label: 'Scorecards' },
        { id: 'compliance', label: 'Compliance' },
        { id: 'audit', label: 'Audit' },
      ]
    },
    { 
      id: 'actions', 
      label: 'Actions', 
      icon: '⚡', 
      color: '#00ff88',
      subTabs: [
        { id: 'crm', label: 'CRM' },
        { id: 'billing', label: 'Billing' },
        { id: 'communications', label: 'Communications' },
        { id: 'scheduling', label: 'Scheduling' },
      ]
    },
    { 
      id: 'training', 
      label: 'Training', 
      icon: '🎓', 
      color: '#ffaa00',
      subTabs: [
        { id: 'personas', label: 'Personas' },
        { id: 'simulator', label: 'Simulator' },
        { id: 'coach', label: 'Coach' },
        { id: 'scenarios', label: 'Scenarios' },
      ]
    },
  ];

  return (
    <nav style={styles.sidebar}>
      {tabs.map(tab => (
        <div key={tab.id} style={styles.navSection}>
          <div style={styles.navSectionTitle}>{tab.label}</div>
          <button
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.subTabs && tab.subTabs.length > 0) {
                setActiveSubTab(tab.subTabs[0].id);
              }
            }}
            style={{
              ...styles.navButton,
              ...(activeTab === tab.id && !tab.subTabs ? styles.navButtonActive : {}),
            }}
          >
            <span style={styles.icon}>{tab.icon}</span>
            {tab.label}
          </button>
          {tab.subTabs && activeTab === tab.id && tab.subTabs.map(subTab => (
            <button
              key={subTab.id}
              onClick={() => setActiveSubTab(subTab.id)}
              style={{
                ...styles.subNavButton,
                ...(activeSubTab === subTab.id ? styles.subNavButtonActive : {}),
              }}
            >
              • {subTab.label}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

// Health Component
function Health(): JSX.Element {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const res = await fetch(`${API_URL}/health`);
        const jsonData = await res.json();
        console.log('[Health] Raw response:', jsonData);
        
        // La réponse est directe, pas enveloppée dans {success, data}
        if (jsonData.status) {
          setData(jsonData);
        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        console.error('[Health] Error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
  }, []);

  if (loading) return <div style={styles.loading}>Chargement...</div>;
  if (error) return <div style={styles.error}>Erreur: {error}</div>;

  return (
    <div>
      <h2 style={styles.pageTitle}>🏥 Health Check</h2>
      <p style={styles.pageDescription}>Status du backend unifié</p>
      <div style={styles.grid}>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, fontSize: '24px'}}>{data?.status === 'ok' ? '✓' : '✗'}</div>
          <div style={styles.statLabel}>Status</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, fontSize: '24px'}}>{data?.version || 'N/A'}</div>
          <div style={styles.statLabel}>Version</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, fontSize: '24px'}}>{data?.environment || 'N/A'}</div>
          <div style={styles.statLabel}>Environment</div>
        </div>
      </div>
      <div style={styles.card}>
        <h3>Détails</h3>
        <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>
      </div>
    </div>
  );
}

// Orchestration Components
function OrchestrationInit(): JSX.Element {
  const [data, setData] = useState<ApiResponse<CallInitData> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const testCall = async () => {
    setLoading(true);
    const res = await apiCall<CallInitData>('/api/v1/call/initiate', 'POST', {
      phoneNumber: '+33612345678',
      initialText: 'Bonjour, j\'ai un problème avec ma facture',
      channel: 'voice',
    });
    setData(res);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📞 Init Call</h3>
      <button onClick={testCall} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '📞 Tester Init Call'}
      </button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function OrchestrationAnalyze(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const testAnalyze = async () => {
    setLoading(true);
    const res = await apiCall('/api/v1/call/analyze-parallel', 'POST', {
      transcript: 'Bonjour, je suis mécontent de votre service',
      sessionId: 'session_test',
    });
    setData(res);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🔍 Analyze Parallel</h3>
      <button onClick={testAnalyze} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '🔍 Analyser'}
      </button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function OrchestrationStatus(): JSX.Element {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    apiCall('/api/v1/call/status').then(setData);
  }, []);

  return (
    <div>
      <h3 style={styles.pageTitle}>📊 ADK Status</h3>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// RAG Components
function RAGEmbedding(): JSX.Element {
  const [data, setData] = useState<EmbeddingData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const testEmbedding = async () => {
    setLoading(true);
    const res = await apiCall<EmbeddingData>('/api/v1/rag/embeddings/generate', 'POST', {
      text: 'Comment obtenir un remboursement ?',
    });
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>✨ Générer Embedding</h3>
      <button onClick={testEmbedding} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '✨ Générer'}
      </button>
      {data && data.success && (
        <div style={{marginTop: '24px'}}>
          <div style={styles.grid}>
            <div style={styles.statCard}>
              <div style={{...styles.statValue, fontSize: '24px'}}>{data.dimensions}</div>
              <div style={styles.statLabel}>Dimensions</div>
            </div>
            <div style={styles.statCard}>
              <div style={{...styles.statValue, fontSize: '16px'}}>{data.model}</div>
              <div style={styles.statLabel}>Model</div>
            </div>
          </div>
          <div style={{...styles.card, marginTop: '16px'}}>
            <h4>First 20 values:</h4>
            <pre style={styles.pre}>[{data.embedding?.slice(0, 20).map(n => n.toFixed(4)).join(', ')}...]</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function RAGSearch(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [query, setQuery] = useState<string>('comment rembourser');
  const [loading, setLoading] = useState<boolean>(false);

  const testSearch = async () => {
    setLoading(true);
    const res = await apiCall('/api/v1/rag/search', 'POST', { query, topK: 5 });
    setData(res);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🔍 Vector Search</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Query:</label>
        <input
          style={styles.input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Entrez votre recherche..."
        />
      </div>
      <button onClick={testSearch} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '🔍 Rechercher'}
      </button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function RAGIngest(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [url, setUrl] = useState<string>('https://example.com/doc.pdf');
  const [text, setText] = useState<string>('Texte à ingérer dans la base de connaissances');
  const [loading, setLoading] = useState<boolean>(false);

  const testIngestUrl = async () => {
    setLoading(true);
    const res = await apiCall('/api/v1/rag/ingest/url', 'POST', { url });
    setData(res);
    setLoading(false);
  };

  const testIngestText = async () => {
    setLoading(true);
    const res = await apiCall('/api/v1/rag/ingest/text', 'POST', { text, metadata: { source: 'manual' } });
    setData(res);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📥 Ingest Documents</h3>
      <div style={styles.card}>
        <h4>Ingest from URL</h4>
        <input style={styles.input} value={url} onChange={(e) => setUrl(e.target.value)} />
        <button onClick={testIngestUrl} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
          {loading ? '⏳ En cours...' : '📥 Ingest URL'}
        </button>
      </div>
      <div style={styles.card}>
        <h4>Ingest Text</h4>
        <textarea style={styles.textarea} value={text} onChange={(e) => setText(e.target.value)} />
        <button onClick={testIngestText} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
          {loading ? '⏳ En cours...' : '📥 Ingest Text'}
        </button>
      </div>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function RAGDocuments(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any>(null);

  const listDocuments = async () => {
    setLoading(true);
    const res = await apiCall('/api/v1/rag/documents');
    setData(res);
    setLoading(false);
  };

  const getStats = async () => {
    const res = await apiCall('/api/v1/rag/stats');
    setStats(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📄 Documents</h3>
      <button onClick={listDocuments} disabled={loading} style={styles.button}>
        {loading ? '⏳ En cours...' : '📋 Lister Documents'}
      </button>
      <button onClick={getStats} style={styles.buttonSecondary}>📊 Stats Index</button>
      {stats && <pre style={styles.pre}>{JSON.stringify(stats, null, 2)}</pre>}
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// Analytics Components
function AnalyticsLive(): JSX.Element {
  const [data, setData] = useState<AnalyticsLive | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const testAnalytics = async () => {
    setLoading(true);
    const res = await apiCall<AnalyticsLive>('/api/v1/data/analytics/live');
    if (res.success && res.data) setData(res.data.live || res.data);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📊 Live Stats</h3>
      <button onClick={testAnalytics} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '🔄 Charger Stats'}
      </button>
      {data && (
        <div style={{marginTop: '24px', ...styles.grid}}>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '36px'}}>{data.activeCalls}</div>
            <div style={styles.statLabel}>Appels en cours</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '36px'}}>{data.waitingCalls}</div>
            <div style={styles.statLabel}>En attente</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '36px'}}>{data.agentsAvailable}</div>
            <div style={styles.statLabel}>Agents disponibles</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '36px'}}>{data.agentsBusy}</div>
            <div style={styles.statLabel}>Agents occupés</div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsDaily(): JSX.Element {
  const [data, setData] = useState<AnalyticsDaily | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const testDaily = async () => {
    setLoading(true);
    const res = await apiCall<AnalyticsDaily>('/api/v1/analytics/daily');
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📈 Daily Stats</h3>
      <button onClick={testDaily} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '📈 Charger Stats'}
      </button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function AnalyticsSentiment(): JSX.Element {
  const [data, setData] = useState<SentimentData | null>(null);
  const [text, setText] = useState<string>('Je suis très mécontent de votre service');
  const [loading, setLoading] = useState<boolean>(false);

  const testSentiment = async () => {
    setLoading(true);
    const res = await apiCall<SentimentData>('/api/v1/sentiment/analyze', 'POST', { text });
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>💭 Sentiment Analysis</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Texte à analyser:</label>
        <textarea style={styles.textarea} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <button onClick={testSentiment} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '💭 Analyser'}
      </button>
      {data && (
        <div style={{marginTop: '24px', ...styles.grid}}>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '24px'}}>{data.sentiment}</div>
            <div style={styles.statLabel}>Sentiment</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '24px'}}>{(data.score * 100).toFixed(0)}%</div>
            <div style={styles.statLabel}>Score</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statValue, fontSize: '24px'}}>{(data.confidence * 100).toFixed(0)}%</div>
            <div style={styles.statLabel}>Confidence</div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsTracking(): JSX.Element {
  const [data, setData] = useState<any>(null);

  const testTracking = async () => {
    const res = await apiCall('/api/v1/tracking/ga4', 'POST', {
      event: 'test_event',
      properties: { category: 'test', action: 'click' },
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📍 Tracking</h3>
      <button onClick={testTracking} style={styles.button}>📍 Track Event GA4</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// Agents Components
function AgentsList(): JSX.Element {
  const [data, setData] = useState<ApiResponse<Agent[]> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const testAgents = async () => {
    setLoading(true);
    const res = await apiCall<Agent[]>('/api/v1/agents');
    setData(res);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🤖 Agents List</h3>
      <button onClick={testAgents} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '📋 Lister Agents'}
      </button>
      {data && data.success && data.data && (
        <div style={{marginTop: '24px'}}>
          {data.data.map((agent: Agent) => (
            <div key={agent.id} style={styles.agentCard}>
              <div style={styles.agentHeader}>
                <div>
                  <div style={styles.agentName}>{agent.name}</div>
                  <div style={styles.agentType}>{agent.type}</div>
                </div>
                <span style={{
                  ...styles.agentStatus,
                  ...(agent.status === 'available' ? styles.statusAvailable :
                      agent.status === 'busy' ? styles.statusBusy : styles.statusOffline),
                }}>
                  {agent.status}
                </span>
              </div>
              <div style={{fontSize: '13px', color: '#9ca3af'}}>
                Spécialités: {agent.specialty.join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Tools(): JSX.Element {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    apiCall('/api/v1/tools').then(setData);
  }, []);

  return (
    <div>
      <h3 style={styles.pageTitle}>🛠️ Tools</h3>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// Memory Components
function MemoryShortTerm(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('session_123');

  const getSTM = async () => {
    const res = await apiCall(`/api/v1/memory/short-term/${sessionId}`);
    setData(res);
  };

  const setSTM = async () => {
    const res = await apiCall(`/api/v1/memory/short-term/${sessionId}`, 'POST', {
      data: { test: 'value', timestamp: Date.now() },
      ttl: 3600000,
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🧠 Short-Term Memory</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Session ID:</label>
        <input style={styles.input} value={sessionId} onChange={(e) => setSessionId(e.target.value)} />
      </div>
      <button onClick={getSTM} style={styles.button}>📖 Get STM</button>
      <button onClick={setSTM} style={styles.buttonSecondary}>💾 Set STM</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function MemoryLongTerm(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [customerId, setCustomerId] = useState<string>('cust_001');

  const getLTM = async () => {
    const res = await apiCall(`/api/v1/memory/long-term/${customerId}`);
    setData(res);
  };

  const setLTM = async () => {
    const res = await apiCall(`/api/v1/memory/long-term/${customerId}`, 'POST', {
      phoneNumber: '+33612345678',
      firstName: 'Jean',
      lastName: 'Dupont',
      email: 'jean@example.com',
      vip: 1,
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🧠 Long-Term Memory</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Customer ID:</label>
        <input style={styles.input} value={customerId} onChange={(e) => setCustomerId(e.target.value)} />
      </div>
      <button onClick={getLTM} style={styles.button}>📖 Get LTM</button>
      <button onClick={setLTM} style={styles.buttonSecondary}>💾 Set LTM</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function MemorySummary(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('session_123');
  const [loading, setLoading] = useState<boolean>(false);

  const generateSummary = async () => {
    setLoading(true);
    const res = await apiCall(`/api/v1/memory/summary/${sessionId}/generate`, 'POST', {
      transcript: 'Customer called about billing issue...',
    });
    setData(res);
    setLoading(false);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📝 Summary Generation</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Session ID:</label>
        <input style={styles.input} value={sessionId} onChange={(e) => setSessionId(e.target.value)} />
      </div>
      <button onClick={generateSummary} disabled={loading} style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}>
        {loading ? '⏳ En cours...' : '📝 Générer Résumé'}
      </button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// QA Components
function QAScorecards(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('session_123');

  const generateScorecard = async () => {
    const res = await apiCall(`/api/v1/qa/scorecards/${sessionId}/generate`);
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>✅ Scorecards</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Session ID:</label>
        <input style={styles.input} value={sessionId} onChange={(e) => setSessionId(e.target.value)} />
      </div>
      <button onClick={generateScorecard} style={styles.button}>✅ Générer Scorecard</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function QACompliance(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [text, setText] = useState<string>('Customer provided credit card number 1234-5678-9012-3456');

  const checkCompliance = async () => {
    const res = await apiCall('/api/v1/qa/compliance/check', 'POST', { transcript: text });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>✅ Compliance Check</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Transcript:</label>
        <textarea style={styles.textarea} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <button onClick={checkCompliance} style={styles.button}>✅ Check Compliance</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function QAAudit(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('session_123');

  const getAuditTrail = async () => {
    const res = await apiCall(`/api/v1/audit/${sessionId}/trail`);
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📋 Audit Trail</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Session ID:</label>
        <input style={styles.input} value={sessionId} onChange={(e) => setSessionId(e.target.value)} />
      </div>
      <button onClick={getAuditTrail} style={styles.button}>📋 Get Audit Trail</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// Actions Components
function ActionsCRM(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [customerId, setCustomerId] = useState<string>('cust_001');

  const getCustomer = async () => {
    const res = await apiCall(`/api/v1/crm/customers/${customerId}`);
    setData(res);
  };

  const createTicket = async () => {
    const res = await apiCall('/api/v1/crm/tickets', 'POST', {
      customerId,
      subject: 'Test ticket',
      description: 'Test description',
      priority: 'medium',
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>⚡ CRM</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Customer ID:</label>
        <input style={styles.input} value={customerId} onChange={(e) => setCustomerId(e.target.value)} />
      </div>
      <button onClick={getCustomer} style={styles.button}>👤 Get Customer</button>
      <button onClick={createTicket} style={styles.buttonSecondary}>🎫 Create Ticket</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function ActionsBilling(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [invoiceId, setInvoiceId] = useState<string>('inv_001');

  const getInvoice = async () => {
    const res = await apiCall(`/api/v1/billing/invoices/${invoiceId}`);
    setData(res);
  };

  const createRefund = async () => {
    const res = await apiCall('/api/v1/billing/refunds', 'POST', {
      invoiceId,
      amount: 50,
      reason: 'Customer request',
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>💰 Billing</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Invoice ID:</label>
        <input style={styles.input} value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} />
      </div>
      <button onClick={getInvoice} style={styles.button}>📄 Get Invoice</button>
      <button onClick={createRefund} style={styles.buttonSecondary}>💰 Create Refund</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function ActionsCommunications(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [email, setEmail] = useState<string>('test@example.com');
  const [phone, setPhone] = useState<string>('+33612345678');

  const sendEmail = async () => {
    const res = await apiCall('/api/v1/communications/email', 'POST', {
      to: email,
      subject: 'Test email',
      body: 'This is a test email',
    });
    setData(res);
  };

  const sendSMS = async () => {
    const res = await apiCall('/api/v1/communications/sms', 'POST', {
      phone,
      message: 'Test SMS',
    });
    setData(res);
  };

  const scheduleCallback = async () => {
    const res = await apiCall('/api/v1/communications/callback', 'POST', {
      phone,
      scheduledAt: new Date().toISOString(),
      notes: 'Test callback',
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📧 Communications</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Email:</label>
        <input style={styles.input} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Phone:</label>
        <input style={styles.input} value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <button onClick={sendEmail} style={styles.button}>📧 Send Email</button>
      <button onClick={sendSMS} style={styles.buttonSecondary}>📱 Send SMS</button>
      <button onClick={scheduleCallback} style={styles.buttonSecondary}>📞 Schedule Callback</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function ActionsScheduling(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [calendarId, setCalendarId] = useState<string>('primary');

  const getAvailability = async () => {
    const res = await apiCall(`/api/v1/scheduling/${calendarId}/availability?date=2026-03-22`);
    setData(res);
  };

  const bookAppointment = async () => {
    const res = await apiCall('/api/v1/scheduling/appointments', 'POST', {
      calendarId,
      startTime: '2026-03-22T10:00:00Z',
      endTime: '2026-03-22T11:00:00Z',
      attendeeEmail: 'test@example.com',
      attendeeName: 'Test User',
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>📅 Scheduling</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Calendar ID:</label>
        <input style={styles.input} value={calendarId} onChange={(e) => setCalendarId(e.target.value)} />
      </div>
      <button onClick={getAvailability} style={styles.button}>📅 Get Availability</button>
      <button onClick={bookAppointment} style={styles.buttonSecondary}>📅 Book Appointment</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// Training Components
function TrainingPersonas(): JSX.Element {
  const [data, setData] = useState<ApiResponse<Persona[]> | null>(null);

  useEffect(() => {
    apiCall<Persona[]>('/api/v1/personas/list').then(setData);
  }, []);

  return (
    <div>
      <h3 style={styles.pageTitle}>🎭 Personas</h3>
      {data && data.success && data.data && (
        <div style={{marginTop: '24px', ...styles.grid}}>
          {data.data.map((persona: Persona) => (
            <div key={persona.id} style={styles.card}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
                <h3 style={{margin: 0, fontSize: '18px'}}>{persona.name}</h3>
                <span style={{
                  ...styles.badge,
                  ...(persona.difficulty === 'easy' ? styles.badgeEasy :
                      persona.difficulty === 'medium' ? styles.badgeMedium : styles.badgeHard),
                }}>
                  {persona.difficulty}
                </span>
              </div>
              <p style={{color: '#9ca3af', fontSize: '14px', marginBottom: '12px'}}>
                {persona.description}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrainingSimulator(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const startSession = async () => {
    const res = await apiCall('/api/v1/simulator/start', 'POST', {
      traineeId: 'trainee_001',
      personaId: 'angry_customer',
      difficulty: 'hard',
    });
    if (res.success) setSessionId(res.data?.sessionId);
    setData(res);
  };

  const sendMessage = async () => {
    if (!sessionId) return;
    const res = await apiCall(`/api/v1/simulator/${sessionId}/message`, 'POST', {
      message: 'Bonjour, comment puis-je vous aider ?',
    });
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🎮 Simulator</h3>
      <button onClick={startSession} style={styles.button}>🎮 Start Session</button>
      {sessionId && <button onClick={sendMessage} style={styles.buttonSecondary}>💬 Send Message</button>}
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function TrainingCoach(): JSX.Element {
  const [data, setData] = useState<any>(null);
  const [traineeId, setTraineeId] = useState<string>('trainee_001');

  const evaluate = async () => {
    const res = await apiCall('/api/v1/coach/evaluate', 'POST', {
      sessionId: 'session_123',
      traineeId,
      conversationHistory: [],
    });
    setData(res);
  };

  const getProgress = async () => {
    const res = await apiCall(`/api/v1/coach/${traineeId}/progress`);
    setData(res);
  };

  return (
    <div>
      <h3 style={styles.pageTitle}>🎓 Coach</h3>
      <div style={styles.formGroup}>
        <label style={styles.label}>Trainee ID:</label>
        <input style={styles.input} value={traineeId} onChange={(e) => setTraineeId(e.target.value)} />
      </div>
      <button onClick={evaluate} style={styles.button}>📊 Evaluate</button>
      <button onClick={getProgress} style={styles.buttonSecondary}>📈 Get Progress</button>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

function TrainingScenarios(): JSX.Element {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    apiCall('/api/v1/scenarios/list').then(setData);
  }, []);

  return (
    <div>
      <h3 style={styles.pageTitle}>📖 Scenarios</h3>
      {data && <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// Main App Component
function App(): JSX.Element {
  const [activeTab, setActiveTab] = useState<string>('health');
  const [activeSubTab, setActiveSubTab] = useState<string>('');

  const renderContent = (): ReactNode => {
    switch (activeTab) {
      case 'health':
        return <Health />;
      
      case 'orchestration':
        switch (activeSubTab) {
          case 'call-initiate': return <OrchestrationInit />;
          case 'call-analyze': return <OrchestrationAnalyze />;
          case 'call-status': return <OrchestrationStatus />;
          default: return <OrchestrationInit />;
        }
      
      case 'rag':
        switch (activeSubTab) {
          case 'embedding': return <RAGEmbedding />;
          case 'search': return <RAGSearch />;
          case 'ingest': return <RAGIngest />;
          case 'documents': return <RAGDocuments />;
          default: return <RAGEmbedding />;
        }
      
      case 'analytics':
        switch (activeSubTab) {
          case 'analytics-live': return <AnalyticsLive />;
          case 'analytics-daily': return <AnalyticsDaily />;
          case 'sentiment': return <AnalyticsSentiment />;
          case 'tracking': return <AnalyticsTracking />;
          default: return <AnalyticsLive />;
        }
      
      case 'agents':
        switch (activeSubTab) {
          case 'agents-list': return <AgentsList />;
          case 'tools': return <Tools />;
          default: return <AgentsList />;
        }
      
      case 'memory':
        switch (activeSubTab) {
          case 'short-term': return <MemoryShortTerm />;
          case 'long-term': return <MemoryLongTerm />;
          case 'summary': return <MemorySummary />;
          default: return <MemoryShortTerm />;
        }
      
      case 'qa':
        switch (activeSubTab) {
          case 'scorecards': return <QAScorecards />;
          case 'compliance': return <QACompliance />;
          case 'audit': return <QAAudit />;
          default: return <QAScorecards />;
        }
      
      case 'actions':
        switch (activeSubTab) {
          case 'crm': return <ActionsCRM />;
          case 'billing': return <ActionsBilling />;
          case 'communications': return <ActionsCommunications />;
          case 'scheduling': return <ActionsScheduling />;
          default: return <ActionsCRM />;
        }
      
      case 'training':
        switch (activeSubTab) {
          case 'personas': return <TrainingPersonas />;
          case 'simulator': return <TrainingSimulator />;
          case 'coach': return <TrainingCoach />;
          case 'scenarios': return <TrainingScenarios />;
          default: return <TrainingPersonas />;
        }
      
      default:
        return <Health />;
    }
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.layout}>
        <Sidebar 
          activeTab={activeTab} 
          activeSubTab={activeSubTab}
          setActiveTab={setActiveTab} 
          setActiveSubTab={setActiveSubTab} 
        />
        <main style={styles.main}>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

// ============================================
// RENDER
// ============================================

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

console.log('[App] Frontend complet (75 routes) mounted successfully');
